import { ErrorInfo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';

import { Box, Loading, TSLErrorBoundary } from '@thirdspacelearning/library';
import { useEventTracking } from '@tsl-frontend/tsl-utilities';
import { useKeycloakAuth, getUserRole } from '@tsl-frontend/tsl-keycloak-auth';

import { DATADOG_EVENT_NAMES } from '@consts';
import { errorPageContentConfig } from '@copy';
import { useOnLoad, useScrollToTop, useStartSession } from '@hooks';
import { APP_TEST } from '@utils';
import { UserActions, SideMenu, SessionDialog } from '@shared';
import { appErrorSelector } from '@store/app';
import { appLoadedSelector } from '@store/initialisation';

function App() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { user, logout } = useKeycloakAuth();

    const pending = useSelector(appLoadedSelector);
    const error = useSelector(appErrorSelector);

    const [sessionDialogOpen, setSessionDialogOpen] = useState(false);

    const { sendUserTracking, sendPageTracking } = useEventTracking(dispatch);

    useScrollToTop();
    useOnLoad();
    const showStartSession = useStartSession();

    useEffect(() => {
        // runs on location, i.e. route, change
        sendPageTracking(location.pathname);
    }, [dispatch, location, sendPageTracking]);

    if (!user) {
        return null;
    }

    const isDashboard = location.pathname.match(/^\/$/);
    const isDialogOpen = !!isDashboard && sessionDialogOpen;

    const contentLines =
        getUserRole(user) && errorPageContentConfig[getUserRole(user)];

    return (
        <Box data-testid={APP_TEST} height="98vh" display="flex">
            <TSLErrorBoundary
                errorScreenContentLines={contentLines}
                onReset={() => navigate(0)}
                onError={(error: Error, info: ErrorInfo) =>
                    sendUserTracking(DATADOG_EVENT_NAMES.ERROR_PAGE_SHOWN, {
                        eventVersion: '1.0.0',
                        error,
                        info,
                    })
                }
                appError={error?.error}
            >
                <SessionDialog
                    showStartSession={showStartSession && !!isDashboard}
                    sessionDialogOpen={sessionDialogOpen}
                    setSessionDialogOpen={setSessionDialogOpen}
                />

                {isDashboard && <SideMenu />}

                <Box display="flex" flexDirection="column" flexGrow={1}>
                    {isDashboard && (
                        <UserActions
                            user={user}
                            logout={logout}
                            dialogOpen={isDialogOpen}
                        />
                    )}

                    {pending ? (
                        <Loading />
                    ) : (
                        <Box flexGrow={1} p={3} mt={6} display="flex">
                            <Outlet />
                        </Box>
                    )}
                </Box>
            </TSLErrorBoundary>
        </Box>
    );
}

export default App;
